import { useEffect } from 'react'

const PIXEL_ID = '25176507425343433'

let pixelLoaded = false
let pixelReady = false
const queuedEvents: string[] = []

// Tracking scripts (CookieYes, Meta Pixel, UTMify) break inside sandboxed
// preview iframes and on localhost, surfacing as cross-origin "Script error."
// Only load them on the real published domain.
export function isTrackingAllowed(): boolean {
  try {
    const host = window.location.hostname
    if (!host) return false
    const blocked =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.local') ||
      host.includes('magicpatterns') ||
      host.includes('webcontainer') ||
      host.includes('stackblitz')
    return !blocked
  } catch (e) {
    return false
  }
}

function loadCookieYes(): void {
  try {
    if (document.getElementById('cookieyes')) return
    const cookieScript = document.createElement('script')
    cookieScript.id = 'cookieyes'
    cookieScript.type = 'text/javascript'
    cookieScript.src =
      'https://cdn-cookieyes.com/client_data/7efe60745ebadb334457b0a9a8f7f593/script.js'
    document.head.appendChild(cookieScript)
  } catch (e) {
    console.warn('CookieYes failed to load:', e)
  }
}

function loadPixelScript(): void {
  if (pixelLoaded) return
  pixelLoaded = true

  try {
    const w = window as any
    if (w.fbq) {
      pixelReady = true
      flushQueue()
      return
    }

    const fbq: any = function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args)
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    w.fbq = fbq
    w._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    // Tag for CookieYes so it recognizes this as a consented marketing script
    script.setAttribute('data-cookieyes', 'cookieyes-marketing')
    document.head.appendChild(script)

    w.fbq('init', PIXEL_ID)
    w.fbq('track', 'PageView')

    pixelReady = true
    flushQueue()
  } catch (e) {
    console.warn('Meta Pixel failed to load:', e)
  }
}

function flushQueue(): void {
  try {
    const w = window as any
    while (queuedEvents.length > 0) {
      const event = queuedEvents.shift()
      if (event && w.fbq) {
        w.fbq('track', event)
      }
    }
  } catch (e) {
    console.warn('Meta Pixel queue flush failed:', e)
  }
}

function hasMarketingConsent(): boolean {
  try {
    const w = window as any
    // CookieYes exposes getCkyConsent() once loaded
    if (typeof w.getCkyConsent === 'function') {
      const consent = w.getCkyConsent()
      if (consent && consent.categories) {
        return !!(
          consent.categories.advertisement ||
          consent.categories.marketing ||
          consent.categories.analytics
        )
      }
    }
    // Fallback: read CookieYes cookie directly
    const match = document.cookie.match(/cookieyes-consent=([^;]+)/)
    if (match) {
      const decoded = decodeURIComponent(match[1])
      // CookieYes cookie format includes "advertisement:yes" or similar
      if (
        decoded.includes('advertisement:yes') ||
        decoded.includes('marketing:yes')
      ) {
        return true
      }
    }
  } catch (e) {
    // ignore
  }
  return false
}

function setupConsentListener(): void {
  try {
    // If consent already given (returning visitor), load immediately
    if (hasMarketingConsent()) {
      loadPixelScript()
      return
    }

    // Listen for CookieYes consent updates
    const handler = (event: any) => {
      try {
        const detail = event?.detail
        const accepted = detail?.accepted || detail?.categories || {}
        const hasConsent =
          (Array.isArray(accepted) &&
            (accepted.includes('advertisement') ||
              accepted.includes('marketing') ||
              accepted.includes('analytics'))) ||
          accepted.advertisement === true ||
          accepted.marketing === true ||
          accepted.analytics === true ||
          accepted.advertisement === 'yes' ||
          accepted.marketing === 'yes' ||
          accepted.analytics === 'yes'

        if (hasConsent) {
          loadPixelScript()
        }
      } catch (e) {
        // If we can't parse the event, load anyway (consent fired = user interacted)
        loadPixelScript()
      }
    }

    document.addEventListener('cookieyes_consent_update', handler)
    document.addEventListener('cookieyes_consent_update_after', handler)
  } catch (e) {
    console.warn('Consent listener setup failed:', e)
  }
}

export function fireAddToCart(): void {
  try {
    const w = window as any
    if (pixelReady && w.fbq) {
      w.fbq('track', 'AddToCart')
    } else {
      // Queue the event for when consent is granted
      queuedEvents.push('AddToCart')
    }
  } catch (e) {
    console.warn('Meta Pixel AddToCart failed:', e)
  }
}

export function useMetaPixel(): void {
  useEffect(() => {
    // Skip all tracking in preview/dev environments to avoid cross-origin
    // "Script error." noise. Scripts run normally on the published domain.
    if (!isTrackingAllowed()) return
    // CookieYes loads first to handle consent
    loadCookieYes()
    // Pixel only loads after user grants consent (or if already granted)
    setupConsentListener()
  }, [])
}
